export type Severity = 'calm' | 'elevated' | 'warning' | 'critical';

/** Left status spine — the app's signature motif. Arbitrary values reference CSS vars so theming/dark-mode work.
 * Spines thicken at wall-display widths (3xl = 1920px) so severity reads from across an EOC. */
export const SEVERITY_SPINE: Record<Severity, string> = {
  calm: '',
  elevated: 'border-l-4 3xl:border-l-[6px] border-l-[color:var(--severity-elevated-spine)]',
  warning: 'border-l-4 3xl:border-l-[6px] border-l-[color:var(--severity-warning-spine)]',
  critical: 'border-l-4 3xl:border-l-[6px] border-l-[color:var(--severity-critical-spine)]',
};

/** Subtle background tint for the severity. */
export const SEVERITY_TINT: Record<Severity, string> = {
  calm: '',
  elevated: 'bg-[color:var(--severity-elevated-bg)]',
  warning: 'bg-[color:var(--severity-warning-bg)]',
  critical: 'bg-[color:var(--severity-critical-bg)]',
};

/** Text color for metric values / labels at a given severity. */
export const SEVERITY_TEXT: Record<Severity, string> = {
  calm: 'text-theme-primary',
  elevated: 'text-[color:var(--severity-elevated-text)]',
  warning: 'text-[color:var(--severity-warning-text)]',
  critical: 'text-[color:var(--severity-critical-text)]',
};

export interface Thresholds {
  warning: number;
  critical: number;
  /** When true, a LOWER value is worse (e.g. remaining capacity). Default false. */
  invert?: boolean;
}

/** Map a numeric value to a severity using thresholds. */
export function severityFromThresholds(value: number, t: Thresholds): Severity {
  const hit = (limit: number) => (t.invert ? value <= limit : value >= limit);
  if (hit(t.critical)) return 'critical';
  if (hit(t.warning)) return 'warning';
  return 'calm';
}
